import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to calculate remaining height for a component
 * @param {number} offset - Additional offset to subtract from calculated height (default: 0)
 * @param {Array} dependencies - Dependencies array to trigger recalculation
 * @returns {Object} - { height: number, ref: React.RefObject }
 */
const useRemainingHeight = (offset = 0, dependencies = []) => {
  const [height, setHeight] = useState(window.innerHeight);
  const ref = useRef(null);
  // console.log("useRemainingHeight called with offset:", height);
  const calculateHeight = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const topPosition = rect.top;
      const windowHeight = window.innerHeight;
      const remainingHeight = windowHeight - topPosition - offset;
      setHeight(Math.max(remainingHeight, 0)); // Ensure height is not negative
    }
  };

  useEffect(() => {
    // Calculate initial height
    calculateHeight();

    // Recalculate on window resize
    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener("resize", handleResize);

    // Recalculate after a short delay to account for any layout shifts
    const timeoutId = setTimeout(calculateHeight, 100);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [offset, ...dependencies]);

  return { height, ref };
};

export default useRemainingHeight;
